import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";
import { useTenantSwitcher } from "shared/context/TenantSwitcherState";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";

type TenantSwitcherProps = {
  compact?: boolean;
  iconOnly?: boolean;
};

// Trigger button only — the actual command palette is rendered once by
// <TenantSwitcherDialog />, shared via TenantSwitcherState so multiple
// buttons (desktop sidebar + mobile navbar) never open independent dialogs.
const TenantSwitcher = ({
  compact = false,
  iconOnly = false,
}: TenantSwitcherProps) => {
  const { hasAccess, openPalette, currentTenantName, isLoading } =
    useTenantSwitcher();

  const shortcutHint = useMemo(
    () =>
      /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform) ? "⌘K" : "Ctrl+K",
    [],
  );

  if (!hasAccess) {
    return null;
  }

  if (iconOnly) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={openPalette}
            aria-label="Open tenant command palette"
            className="flex items-center justify-center transition rounded-md size-9 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <FontAwesomeIcon
              icon={isLoading ? "rotate" : "building"}
              className={isLoading ? "animate-spin" : ""}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {isLoading ? "Switching tenant..." : currentTenantName}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <button
      onClick={openPalette}
      aria-label="Open tenant command palette"
      className={
        compact
          ? "group flex h-11 w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
          : "group flex h-[50px] min-w-[220px] items-center justify-between rounded-lg border border-slate-200 bg-white px-4 text-sm shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
      }
    >
      <span className="min-w-0 truncate font-medium text-slate-800">
        {isLoading ? "Switching tenant..." : currentTenantName}
      </span>
      <p className="ml-3 flex items-center gap-2 text-xs text-slate-400">
        {!compact && (
          <span className="hidden rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-500 sm:inline">
            {shortcutHint}
          </span>
        )}
        <FontAwesomeIcon
          icon={isLoading ? "rotate" : "chevron-right"}
          className={isLoading ? "animate-spin text-slate-500" : ""}
        />
      </p>
    </button>
  );
};

export default TenantSwitcher;
